import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { ArrowLeft, ExternalLink, Loader2, MapPin, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { detailPathForKind } from "@/lib/kind-labels"
import { producaoApi, errorMessage, type Job, type JobStatus } from "./api"
import { useProducao } from "./context"
import { Initials, MaterialStatePill, Pill, TrelloSyncPill, VanPill } from "./components"
import { useMaterial, useMaterialRefresh } from "./material"
import { ClosureTab, FieldLogTab } from "./Campo"
import { selectClass } from "./AssignPanel"
import { ContractGuidePanel, contractStateOf } from "./ContractGuide"
import { fmtDay, fmtEur, hasFieldStop, jobDays, kindText, koortexContractHref, STATUS_LABEL, teamOf, vanOf } from "./lib"
import { confirmDialog, promptDialog } from "@/lib/confirm"

const STATUS_TONE: Record<JobStatus, "success" | "warning" | "danger" | "neutral" | "info"> = {
  por_atribuir: "neutral",
  planeada: "info",
  execucao: "warning",
  parada: "danger",
  concluida: "success",
  cancelada: "neutral",
}

/** G4 · Detalhe da obra — Resumo e Material (Fase 1); Registo de campo e Fecho (Fase 3). */
export function Detalhe() {
  const { id } = useParams<{ id: string }>()
  const { shell, board, openAssign, upsertJob, reload } = useProducao()
  const [job, setJob] = useState<Job | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // true = abrir sem nota; string = abrir com a nota pré-preenchida.
  const [suspendOpen, setSuspendOpen] = useState<boolean | string>(false)
  const [completeOpen, setCompleteOpen] = useState(false)

  // O quadro já tem a obra; o GET garante a versão fresca (e links diretos).
  const fromBoard = board?.jobs.find((j) => j.id === id) ?? null
  useEffect(() => {
    if (!shell || !id) return
    producaoApi.job(shell, id).then(setJob).catch((err) => setError(errorMessage(err)))
  }, [shell, id, fromBoard?.version, fromBoard?.trello_sync_status])

  const current = job ?? fromBoard
  if (error && !current) return <p className="py-24 text-center text-sm text-destructive">{error}</p>
  if (!current || !board) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin" /></div>

  const change = async (body: Parameters<typeof producaoApi.patchJob>[2], okMsg: string) => {
    if (!shell) return
    setBusy(true)
    try {
      const updated = await producaoApi.patchJob(shell, current.id, { ...body, version: current.version })
      setJob(updated)
      upsertJob(updated)
      shell.bridge.toast({ variant: "success", title: okMsg })
      // Modo `host`: o estado mudou no contrato — relê o guia e o quadro.
      if (board.config.contracts_backend === "host") await refreshContract()
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Não foi possível", description: errorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  const refreshContract = async () => {
    if (!shell) return
    const fresh = await producaoApi.job(shell, current.id).catch(() => null)
    if (fresh) setJob(fresh)
    await reload()
  }
  /** «Ver no Koortex»: a ficha do contrato no CRM (o host navega — `navigate.crm`). */
  const openInKoortex = () => {
    if (!shell || !current.contract_id) return
    const locale = (shell.context as { locale?: string } | undefined)?.locale
    shell.bridge.navigateCrm({ href: koortexContractHref(current.contract_id, locale) })
  }
  // `initial`: a nota pré-preenchida (o motivo comunicado pelo técnico).
  const suspend = async (initial?: string) => {
    // Modo `host`: o motivo vem da lista do Koortex (paragem do contrato).
    if (board.config.contracts_backend === "host") return setSuspendOpen(initial ? initial : true)
    const reason = await promptDialog("Motivo da paragem", { initial: initial ?? "", confirmLabel: "Suspender" })
    if (reason?.trim()) void change({ status: "parada", stop_reason: reason.trim() }, "Obra suspensa")
  }
  const dismissFieldStop = async () => {
    if (!shell) return
    setBusy(true)
    try {
      const updated = await producaoApi.dismissFieldStop(shell, current.id)
      setJob({ ...updated, contract_guide: current.contract_guide })
      upsertJob(updated)
      shell.bridge.toast({ variant: "success", title: "Paragem comunicada dispensada" })
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Não foi possível", description: errorMessage(err) })
    } finally {
      setBusy(false)
    }
  }
  const cancel = async () => {
    if (await confirmDialog("Cancelar esta obra? O cartão no Trello é arquivado.", { destructive: true, confirmLabel: "Cancelar obra", cancelLabel: "Voltar" })) void change({ status: "cancelada" }, "Obra cancelada")
  }
  // Modo `host`: a obra sem contrato (anterior ao modo, ou cujo contrato falhou).
  const hostMode = board.config.contracts_backend === "host"
  const guide = hostMode ? current.contract_guide ?? null : null
  const canSuspend = (current.status === "planeada" || current.status === "execucao") && !guide?.on_hold
  // Decisão 7: o gestor conclui no Detalhe (contrato adjudicado, não parado, aceitação por registar).
  const canCompleteInContract =
    hostMode &&
    !!guide &&
    !guide.on_hold &&
    ["planear", "executar", "aceitar"].includes(guide.current_stage_key ?? "") &&
    (current.status === "planeada" || current.status === "execucao" || current.status === "concluida")
  const createContract = async () => {
    if (!shell) return
    setBusy(true)
    try {
      const out = await producaoApi.createContract(shell, current.id)
      setJob(out.job)
      upsertJob(out.job)
      shell.bridge.toast({ variant: "success", title: "Contrato criado no Koortex" })
      await refreshContract()
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Contrato por criar", description: errorMessage(err) })
    } finally {
      setBusy(false)
    }
  }
  const resync = async () => {
    if (!shell) return
    setBusy(true)
    try {
      const updated = await producaoApi.syncJob(shell, current.id)
      setJob({ ...updated, technician_ids: current.technician_ids })
      upsertJob({ ...updated, technician_ids: current.technician_ids })
    } finally {
      setBusy(false)
    }
  }

  const team = teamOf(current, board.technicians)
  const days = jobDays(current)
  const van = vanOf(current, board.vans)
  const proposalPath = current.proposal_id && current.kind ? detailPathForKind(current.kind, current.proposal_id) : null
  const mapsUrl = current.address || current.city
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([current.address, current.city].filter(Boolean).join(", "))}`
    : null
  const open = current.status !== "concluida" && current.status !== "cancelada"

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <Link to="/producao" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Quadro
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="break-words text-xl font-semibold">{current.client_name}</h1>
            {hostMode && guide ? (
              <Pill tone={contractStateOf(guide).tone}>{contractStateOf(guide).label}{guide.current_phase && !guide.on_hold ? ` · ${guide.current_phase.name}` : ""}</Pill>
            ) : (
              <Pill tone={STATUS_TONE[current.status]}>{STATUS_LABEL[current.status]}</Pill>
            )}
            <TrelloSyncPill job={current} configured={board.config.trello_configured} />
          </div>
          <p className="text-sm text-muted-foreground">
            {[kindText(current), [current.address, current.city].filter(Boolean).join(", "), fmtEur(current.value_eur)].filter(Boolean).join(" · ")}
          </p>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span>{days.length ? (days.length > 1 ? `${fmtDay(days[0]!)} → ${fmtDay(days[days.length - 1]!)} (${days.length} dias)` : fmtDay(days[0]!)) : "Sem data"}</span>
            {current.van_id && <VanPill van={van} />}
            <Initials techs={team} />
            {team.length > 0 && <span className="text-xs text-muted-foreground">{team.map((t) => t.name).join(", ")}</span>}
          </div>
          {hostMode && current.contract_resumed && (
            <p className="text-sm text-muted-foreground">Retomada no Koortex: a paragem foi levantada no contrato e a obra voltou a andar na app.</p>
          )}
          {hostMode && guide && (guide.status === "cancelled" || guide.general_state === "cancelado") && (
            <p className="text-sm text-muted-foreground">Cancelada no Koortex: a obra foi cancelada na app e o cartão do Trello arquivado.</p>
          )}
          {hostMode && guide?.on_hold ? (
            <p className="text-sm text-red-700">
              Parada pelo gestor: {[guide.on_hold.reason_label, guide.on_hold.note].filter(Boolean).join(" · ") || "sem motivo"}
            </p>
          ) : current.status === "parada" && current.stop_reason && (
            <p className="text-sm text-red-700">Parada: {current.stop_reason}</p>
          )}
        </div>
        <div className="flex w-full flex-wrap gap-2 md:w-auto">
          {/* Decisão 3: em `host` só as obras de uma ordem de venda têm contrato. */}
          {hostMode && !current.contract_id && current.status !== "cancelada" && current.odoo_sale_order_id != null && (
            <Button variant="outline" onClick={createContract} disabled={busy} title="A obra ainda não tem contrato no Koortex">
              Criar contrato
            </Button>
          )}
          {current.contract_id && (
            <Button variant="outline" onClick={openInKoortex} title="Abrir a ficha do contrato no Koortex">
              Ver no Koortex <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          )}
          {open && (
            <Button onClick={() => openAssign({ job: current })} disabled={busy}>
              {current.status === "por_atribuir" ? "Atribuir" : "Reatribuir"}
            </Button>
          )}
          {canSuspend && (
            <Button variant="outline" onClick={() => void suspend()} disabled={busy}>Suspender</Button>
          )}
          {current.status === "parada" && (
            <Button variant="outline" onClick={() => change({ status: "planeada" }, "Obra retomada")} disabled={busy}>Retomar</Button>
          )}
          {current.status === "planeada" && (
            <Button variant="outline" onClick={() => change({ status: "execucao" }, "Obra em execução")} disabled={busy} title="Normalmente é a app de campo que marca o início">
              Marcar em execução
            </Button>
          )}
          {/* Modo `host`: o cancelamento faz-se no contrato do Koortex; concluir também aqui (decisão 7), com nota. */}
          {!hostMode && (current.status === "execucao" || current.status === "planeada") && (
            <Button variant="outline" onClick={() => change({ status: "concluida" }, "Obra concluída")} disabled={busy}>Concluir</Button>
          )}
          {canCompleteInContract && (
            <Button variant="outline" onClick={() => setCompleteOpen(true)} disabled={busy} title="Sem assinatura do cliente — normalmente é o fecho de obra no telemóvel">
              Concluir
            </Button>
          )}
          {!hostMode && open && (
            <Button variant="ghost" className="text-destructive" onClick={cancel} disabled={busy}>Cancelar obra</Button>
          )}
          {current.status === "cancelada" && shell?.isAdmin && (
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => change({ status: "por_atribuir" }, "Obra reaberta · por atribuir")}
            >
              Reabrir obra
            </Button>
          )}
        </div>
      </header>

      {completeOpen && (
        <CompleteDialog
          closureError={current.field_closure_error ?? null}
          onClose={() => setCompleteOpen(false)}
          onConfirm={(note) => {
            setCompleteOpen(false)
            void change({ status: "concluida", completion_note: note }, "Obra concluída")
          }}
        />
      )}

      {suspendOpen && (
        <SuspendDialog
          initialNote={typeof suspendOpen === "string" ? suspendOpen : ""}
          onClose={() => setSuspendOpen(false)}
          onConfirm={(reason, note) => {
            setSuspendOpen(false)
            void change({ status: "parada", hold_reason_id: reason.id, stop_reason: note ? `${reason.label} · ${note}` : reason.label }, "Obra suspensa")
          }}
        />
      )}

      {current.status === "parada" && current.legacy_field_stop && (
        <section className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" aria-live="polite">
          <div className="min-w-0">
            <p className="font-medium">Parada pelo técnico (regra antiga) — Retomar?</p>
            <p className="text-xs">
              {board.technicians.find((t) => t.id === current.legacy_field_stop!.technician_id)?.name ?? "Técnico"}
              {` · ${new Date(current.legacy_field_stop.occurred_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}`}
              {current.legacy_field_stop.reason && ` · ${current.legacy_field_stop.reason}`}
              {" · hoje o telemóvel só comunica paragens: sem o gestor, a obra fica parada."}
            </p>
          </div>
          <Button size="sm" onClick={() => change({ status: "planeada" }, "Obra retomada")} disabled={busy}>Retomar</Button>
        </section>
      )}

      {hasFieldStop(current) && (
        <section className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" aria-live="polite">
          <div className="min-w-0">
            <p className="font-medium">Paragem comunicada pelo técnico: {current.field_stop_reason ?? "sem motivo"}</p>
            <p className="text-xs">
              {board.technicians.find((t) => t.id === current.field_stop_technician_id)?.name ?? "Técnico"}
              {current.field_stop_at && ` · ${new Date(current.field_stop_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}`}
              {" · o telemóvel não para a obra: suspende ou dispensa."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canSuspend && (
              <Button size="sm" onClick={() => void suspend(current.field_stop_reason ?? "")} disabled={busy}>
                Suspender obra
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={dismissFieldStop} disabled={busy}>
              Dispensar
            </Button>
          </div>
        </section>
      )}

      <Tabs defaultValue="resumo">
        <TabsList className="grid w-full grid-cols-4 sm:inline-flex sm:w-auto">
          <TabsTrigger value="resumo">Resumo</TabsTrigger>
          <TabsTrigger value="material">Material</TabsTrigger>
          <TabsTrigger value="campo">
            <span className="sm:hidden">Campo</span>
            <span className="hidden sm:inline">Registo de campo</span>
          </TabsTrigger>
          <TabsTrigger value="fecho">Fecho</TabsTrigger>
        </TabsList>

        <TabsContent value="resumo">
          {hostMode && (
            <div className="mb-4">
              {guide ? (
                <ContractGuidePanel guide={guide} />
              ) : (
                <p className="rounded-xl border p-4 text-sm text-muted-foreground border-border">
                  {current.contract_error
                    ? `Não foi possível ler o contrato no Koortex: ${current.contract_error}`
                    : current.contract_id
                      ? "A carregar o contrato…"
                      : current.odoo_sale_order_id == null
                        ? "Obra sem ordem de venda no Odoo: com as obras no Koortex, não tem contrato."
                        : "Esta obra ainda não tem contrato no Koortex («Criar contrato»)."}
                </p>
              )}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <section className="space-y-2 rounded-xl border p-4 border-border">
              <h2 className="text-sm font-semibold">Ligações</h2>
              <dl className="grid grid-cols-[104px_minmax(0,1fr)] gap-x-2 gap-y-2 text-sm sm:grid-cols-[140px_minmax(0,1fr)]">
                <dt className="text-muted-foreground">Morada</dt>
                <dd>
                  {mapsUrl ? (
                    <a href={mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-start gap-1 break-words text-accent hover:underline">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {[current.address, current.city].filter(Boolean).join(", ")}
                    </a>
                  ) : "—"}
                </dd>
                <dt className="text-muted-foreground">Proposta</dt>
                <dd>{proposalPath ? <Link to={proposalPath} className="text-accent hover:underline">Abrir no simulador</Link> : "—"}</dd>
                <dt className="text-muted-foreground">Ordem de venda</dt>
                <dd>{current.odoo_sale_order_name ?? "Sem ordem no Odoo"}</dd>
                <dt className="text-muted-foreground">Cartão no Trello</dt>
                <dd className="flex flex-wrap items-center gap-2">
                  {current.trello_card_url ? (
                    <a href={current.trello_card_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">
                      Abrir <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="text-muted-foreground">{current.status === "por_atribuir" ? "Nasce ao atribuir" : "Ainda sem cartão"}</span>
                  )}
                  {(current.trello_sync_status === "pendente" || current.trello_sync_status === "erro") && (
                    <Button size="sm" variant="ghost" className="h-7" onClick={resync} disabled={busy}>
                      <RefreshCw className="h-3.5 w-3.5" /> Sincronizar
                    </Button>
                  )}
                </dd>
              </dl>
              {current.trello_error && current.trello_sync_status !== "ok" && (
                <p className="break-words text-xs text-muted-foreground">Último erro do Trello: {current.trello_error}</p>
              )}
            </section>
            <NoteCard job={current} disabled={busy || !open} onSave={(note) => change({ note }, "Recado guardado")} />
          </div>
        </TabsContent>

        <TabsContent value="material">
          <MaterialTab job={current} />
        </TabsContent>

        <TabsContent value="campo">
          <FieldLogTab job={current} />
        </TabsContent>

        <TabsContent value="fecho">
          <ClosureTab job={current} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function NoteCard({ job, disabled, onSave }: { job: Job; disabled: boolean; onSave(note: string | null): void }) {
  const [note, setNote] = useState(job.note ?? "")
  useEffect(() => setNote(job.note ?? ""), [job.note])
  return (
    <section className="space-y-2 rounded-xl border p-4 border-border">
      <h2 className="text-sm font-semibold">Recado para a equipa</h2>
      <textarea
        rows={4}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={disabled}
        aria-label="Recado para a equipa"
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm"
        placeholder="Aparece na app como «Recado do escritório»"
      />
      <div className="flex justify-end">
        <Button size="sm" variant="outline" disabled={disabled || note === (job.note ?? "")} onClick={() => onSave(note.trim() || null)}>
          Guardar recado
        </Button>
      </div>
    </section>
  )
}

function MaterialTab({ job }: { job: Job }) {
  const hasOrder = job.odoo_sale_order_id != null
  const st = useMaterial(job.id, hasOrder)
  const refresh = useMaterialRefresh()
  if (!hasOrder) return <p className="rounded-xl border p-6 text-sm text-muted-foreground border-border">Obra sem ordem de venda no Odoo — não há material para ler.</p>
  return (
    <section className="space-y-3 rounded-xl border p-4 border-border">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold">Material da obra</h2>
          <MaterialStatePill state={st} />
        </div>
        <Button size="sm" variant="ghost" onClick={() => refresh(job.id)}>
          <RefreshCw className="h-3.5 w-3.5" /> Atualizar do Odoo
        </Button>
      </div>
      {st?.lines && st.lines.length > 0 ? (
        <div className="-mx-4 overflow-x-auto px-4">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground border-border">
              <th className="py-2 font-medium">Artigo</th>
              <th className="py-2 text-right font-medium">Orçamentado</th>
              <th className="py-2 text-right font-medium">Reservado no Odoo</th>
              <th className="py-2 text-right font-medium">Em falta</th>
              <th className="py-2 text-right font-medium">Satisfeito</th>
            </tr>
          </thead>
          <tbody>
            {st.lines.map((l, i) => (
              <tr key={i} className="border-b last:border-0 border-border">
                <td className="py-2">
                  {l.product}
                  {l.default_code && <span className="ml-1 text-xs text-muted-foreground">{l.default_code}</span>}
                </td>
                <td className="py-2 text-right">{l.demand} {l.uom}</td>
                <td className="py-2 text-right">{l.reserved}</td>
                <td className={l.missing > 0 ? "py-2 text-right font-medium text-amber-700" : "py-2 text-right text-muted-foreground"}>{l.missing || "—"}</td>
                <td className="py-2 text-right text-muted-foreground">—</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {st?.status === "pronto" ? "Tudo entregue ou reservado." : st?.status === "sem_transferencia" ? "A ordem ainda não tem transferências no Odoo." : st?.error ?? "A carregar…"}
        </p>
      )}
      <p className="text-xs text-muted-foreground">Reservado vem do picking no Odoo. Satisfeito chega da app quando a equipa fecha a obra.</p>
    </section>
  )
}

/**
 * Concluir em modo `host` (decisão 7): sem assinatura do cliente, com nota
 * obrigatória — vai para o contrato como «Concluída pelo gestor <nome> na
 * Produção. Nota: …», e os itens da lista «Instalação» ficam marcados.
 */
function CompleteDialog({
  closureError,
  onClose,
  onConfirm,
}: {
  closureError: { signed: boolean; error: string | null } | null
  onClose(): void
  onConfirm(note: string): void
}) {
  const [note, setNote] = useState("")
  // M4: com um fecho do técnico com assinatura em erro, concluir aqui perdia a assinatura.
  const blocked = !!closureError?.signed
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Concluir obra</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {closureError && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
              {closureError.signed
                ? "Há um fecho do técnico com assinatura em erro — tente de novo primeiro (separador Campo → «Tentar de novo»)."
                : "Há um fecho do técnico em erro — tente de novo primeiro (separador Campo); concluir aqui regista a aceitação sem assinatura."}
              {closureError.error && <span className="mt-1 block text-xs">{closureError.error}</span>}
            </p>
          )}
          <p className="text-muted-foreground">
            Sem assinatura do cliente. A lista «Instalação» fica marcada e a aceitação vai para o contrato como «Concluída pelo gestor na Produção», com a tua nota.
          </p>
          <div className="space-y-1">
            <Label htmlFor="conc-note">Nota (obrigatória)</Label>
            <Input id="conc-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Porque fica concluída sem o fecho no telemóvel" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!note.trim() || blocked} onClick={() => onConfirm(note.trim())}>Concluir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Suspender em modo `host`: motivo da lista do Koortex (`hold_reasons`) + nota. */
function SuspendDialog({
  initialNote = "",
  onClose,
  onConfirm,
}: {
  initialNote?: string
  onClose(): void
  onConfirm(reason: { id: number; label: string }, note: string | null): void
}) {
  const { shell } = useProducao()
  const [reasons, setReasons] = useState<Array<{ id: number; label: string }> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reasonId, setReasonId] = useState<number | null>(null)
  const [note, setNote] = useState(initialNote)
  useEffect(() => {
    if (!shell) return
    producaoApi
      .contractOptions(shell)
      .then((o) => setReasons(o.hold_reasons))
      .catch((err) => setError(errorMessage(err)))
  }, [shell])
  const reason = reasons?.find((r) => r.id === reasonId) ?? null
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Suspender obra</DialogTitle>
        </DialogHeader>
        {error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : !reasons ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="sus-reason">Motivo</Label>
              <select id="sus-reason" className={selectClass} value={reasonId ?? ""} onChange={(e) => setReasonId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Escolher…</option>
                {reasons.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="sus-note">Nota (opcional)</Label>
              <Input id="sus-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="O que falta, até quando…" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!reason} onClick={() => reason && onConfirm(reason, note.trim() || null)}>Suspender</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
